import {
  useInfiniteScroll,
  useLazyGetDemosByRecordingQuery,
} from '@carboncopyco/data';
import { DemoIcon } from '@carboncopyco/icons';
import type { IDemo } from '@carboncopyco/types';
import {
  ColorHex,
  Grid,
  SkeletonImageLoading,
  TextButton,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ScreenshotTile } from '../../../GridTiles';
import { InfiniteList } from '../../../InfiniteList';
import { EmptyState } from '../../EmptyState';
import { TitleCard } from '../../InfoPageCards/TitleCard';
import { useClonePageProps } from '../ClonePageContext';

export const DemosCard = () => {
  const { organizationId, cloneId, demoPagePath } = useClonePageProps();
  const {
    items: demos,
    isLoading: isLoadingDemos,
    error: errorDemo,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
  } = useInfiniteScroll<IDemo>(useLazyGetDemosByRecordingQuery);

  const fetchDemosArgs = {
    organizationId,
    recordingId: cloneId,
  };

  useEffect(() => {
    fetchFirstBatch(fetchDemosArgs);
  }, [organizationId, cloneId]);

  return (
    <TitleCard
      header={
        <div className="flex items-center gap-2 p-6">
          <TextLargeParagraph
            color="text-ds-natural-700"
            label="Demos"
          />
          {demos && <TextLargeParagraph label={`(${demos.length})`} />}
        </div>
      }
      radius
    >
      {!isLoadingDemos && !demos?.length ? (
        <EmptyState
          title="No demos were made from this clone yet"
          description='To create a demo from this clone, click "Create demo"'
        />
      ) : (
        <div className="min-h-[290px] max-h-[350px] h-full flex flex-col justify-center p-6 overflow-auto overflow">
          <InfiniteList
            onReachBottom={() => fetchNextBatch(fetchDemosArgs)}
            error={
              errorDemo && {
                message: 'Error fetching screens.',
              }
            }
            isLoading={isLoadingDemos}
            loader={
              <SkeletonImageLoading
                cols={4}
                rows={2}
              />
            }
            isLoadingBatch={isLoadingBatch}
          >
            <Grid<IDemo>
              gap="xl"
              data={demos}
              renderItem={(demo) => (
                <div className="pr-1 mb-6">
                  <AspectRatio ratio={16 / 10}>
                    <ScreenshotTile
                      title={demo.demoName}
                      image={demo.image}
                      overlay={
                        <Link
                          className={classNames(
                            'absolute inset-0 z-45 bg-ds-black bg-opacity-50 cursor-pointer rounded-md'
                          )}
                          to={demoPagePath?.(demo.id)}
                        >
                          <div className="h-full flex justify-center items-center truncate gap-2">
                            <DemoIcon color={ColorHex.accent3100} />
                            <TextButton
                              label="Demo info"
                              color="text-ds-accent3-100"
                            />
                          </div>
                        </Link>
                      }
                    />
                  </AspectRatio>
                </div>
              )}
            />
          </InfiniteList>
        </div>
      )}
    </TitleCard>
  );
};
